import React, { createContext, useContext } from 'react';
import type { PatientReadOnlyReason, PatientRecordResponse } from '../../../../services/patientRecordService';
import type { PatientStatus } from '../../../../services/patientStatusService';

/**
 * PRX-80 — R5's frontend half: ONE flag for the whole Patient 360 page.
 *
 * `Patient360Refactored` resolves this with `resolvePatient360ReadOnly()` below
 * — fed by `usePatientRecord()` AND the existing, always-live `usePatientStatus`
 * as a fallback (R6, refined) — and wraps the tab panels in the provider;
 * anything under the tab panels reads it with `usePatient360ReadOnly()` rather
 * than re-deriving its own opinion of whether this patient's file may be edited.
 */
export interface Patient360ReadOnly {
  readOnly: boolean;
  reason: PatientReadOnlyReason | null;
  /** Sentence-case explanation for a tooltip or a disabled control's title. */
  message: string | null;
}

/** One message per reason — sentence case, no exclamation marks. The same
 * words everywhere a control needs to say why it is disabled, so the banner
 * and a tooltip never drift into two different explanations. */
export const PATIENT_360_READ_ONLY_MESSAGES: Record<PatientReadOnlyReason, string> = {
  inactive: 'This patient is inactivated. Their file is read-only on this page.',
  deceased: 'This patient is recorded as deceased. Their file is read-only on this page.',
  transferred: 'This patient was transferred out. Their file is read-only on this page.',
  not_in_phox: 'This patient is not in PHOX yet. Their file is read-only on this page until their first order or sync.',
};

/**
 * PRX-80 Task 8, R-4 — the backend can answer `readOnly: true` with a NULL
 * reason (an unknown status fails closed, same as this page's own loading
 * default below), so the banner needs a sentence for that case too. Every
 * sentence here says "on this page" / "to edit their file" — never "locked" —
 * because other screens (order-fulfilment) can still write phones, addresses
 * and delivery preferences while Patient 360 shows this banner (R-3).
 */
export const PATIENT_360_READ_ONLY_UNKNOWN_MESSAGE = 'This file is read-only on this page.';

function messageForReason(reason: PatientReadOnlyReason | null): string {
  return reason ? PATIENT_360_READ_ONLY_MESSAGES[reason] : PATIENT_360_READ_ONLY_UNKNOWN_MESSAGE;
}

/** The default outside any provider — and the pre-answer default before the
 * record read settles is a separate, deliberate choice (see below): a page
 * that does not yet know whether a patient may be edited is not a page that
 * may be edited. */
const WRITABLE: Patient360ReadOnly = { readOnly: false, reason: null, message: null };

const Patient360ReadOnlyReactContext = createContext<Patient360ReadOnly>(WRITABLE);

export const Patient360ReadOnlyProvider: React.FC<{
  value: Patient360ReadOnly;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <Patient360ReadOnlyReactContext.Provider value={value}>{children}</Patient360ReadOnlyReactContext.Provider>
);

/** `{ readOnly: false, ... }` with no provider — never a lockout by accident. */
export function usePatient360ReadOnly(): Patient360ReadOnly {
  return useContext(Patient360ReadOnlyReactContext);
}

/** Read-only, no reason — the first-load default before anything has
 * answered. Carries the same unknown-reason sentence as the record read's own
 * `readOnly: true, readOnlyReason: null` answer (R-4) — the banner must not
 * render blank just because THIS particular read-only state came from the
 * loading fallback rather than a settled record. */
const LOADING_UNKNOWN: Patient360ReadOnly = {
  readOnly: true,
  reason: null,
  message: PATIENT_360_READ_ONLY_UNKNOWN_MESSAGE,
};

export type Patient360RecordQueryState = 'loading' | 'success' | 'error';

/**
 * `usePatientRecord()`'s state, reduced to what `resolvePatient360ReadOnly`
 * needs, plus the live PRX-36 patient status (`usePatientStatus().data?.status`)
 * it falls back to. Kept as plain data (not a `UseQueryResult`) so the
 * resolution stays testable without mounting a `QueryClientProvider`.
 */
export interface ResolvePatient360ReadOnlyInput {
  /** Present only when `recordState === 'success'`. */
  record?: Pick<PatientRecordResponse, 'readOnly' | 'readOnlyReason'>;
  recordState: Patient360RecordQueryState;
  /** `null`/`undefined` while the status read is itself still loading or has failed. */
  status?: PatientStatus | null;
}

/** The status-only mapping used as the record read's fallback. `null` for a
 * status this function does not need to (or, for `active`, deliberately does
 * not) turn into a `PatientReadOnlyReason` on its own. */
function readOnlyForStatus(status: PatientStatus): Patient360ReadOnly | null {
  if (status === 'active') return WRITABLE;
  if (status === 'inactive' || status === 'deceased' || status === 'transferred') {
    return { readOnly: true, reason: status, message: messageForReason(status) };
  }
  return null;
}

/**
 * Resolves the page's one read-only flag. The record read (Task 1's endpoint)
 * is the richer source — it is the only one that can say `not_in_phox` — but
 * it is one query among others, and must not be the ONLY thing standing
 * between an inactivated patient and an editor that says nothing about it.
 * `usePatientStatus` (`GET …/status`) is a working, always-live endpoint
 * today, so it is the fallback whenever the record read cannot yet, or ever,
 * answer.
 *
 * Resolution order:
 * 1. **Record query succeeded** → its own `readOnly`/`readOnlyReason` wins
 *    outright.
 * 2. **The live status is already known** (whether the record query is still
 *    loading or has errored) → map it directly: inactive/deceased/transferred
 *    → that reason; active → writable. A known status is never made to wait
 *    on, or be overridden by, the record read's own timing.
 * 3. **Record query still loading, status not yet known either** → read-only,
 *    no reason, until something answers.
 * 4. **Record query errored (a 404 before Task 1's endpoint ships, or a
 *    genuine failure) and the status is unknown too** → writable, no reason —
 *    a double failure locks nothing new and toasts nothing, exactly how this
 *    page behaved before either read existed.
 */
export function resolvePatient360ReadOnly({
  record,
  recordState,
  status,
}: ResolvePatient360ReadOnlyInput): Patient360ReadOnly {
  if (recordState === 'success' && record) {
    const reason = record.readOnlyReason;
    // A writable answer carries no message — only a read-only one needs a
    // sentence, and `messageForReason(null)` no longer returns null (R-4).
    return {
      readOnly: record.readOnly,
      reason,
      message: record.readOnly ? messageForReason(reason) : null,
    };
  }

  const fromStatus = status ? readOnlyForStatus(status) : null;
  if (fromStatus) return fromStatus;

  return recordState === 'loading' ? LOADING_UNKNOWN : WRITABLE;
}
